import moment from "moment";
import { useState } from "react";
import { connect } from "react-redux";
import { approveRefundRequest } from "../../stores/actions/refund-request.actions.types";
import { BiLoaderAlt } from "react-icons/bi";
import { Modal } from "@mantine/core";

const RefundRequestTable = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleApprove = () => {
    setApproveLoading(true);
    const callback = (data) => {
      if (data.success) {
        setApproveLoading(false);
        setIsModalOpen(false);
        props.getRefundRequestList();
      } else {
        setApproveLoading(false);
      }
    };
    props.approveRefundRequest({ id: selectedRequest?._id, callback });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      rejected:
        "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status || "pending"}
      </span>
    );
  };

  return (
    <div className='rounded-[10px] bg-white drop-shadow-1 border-l border-slate-300 dark:border-slate-700 dark:bg-boxdark dark:drop-shadow-none border border-stroke bg-white p-4 shadow-1 dark:border-dark-3 dark:bg-gray-dark dark:shadow-card sm:p-2.5 transition-colors duration-300'>
      <div className='max-w-full overflow-x-auto overflow-y-hidden pb-6'>
        <table className='w-full table-auto'>
          <thead>
            <tr className='bg-[#F7F9FC] dark:bg-black text-left dark:bg-dark-2'>
              <th className='min-w-[200px] px-4 py-2 font-medium text-dark dark:text-white xl:pl-7.5'>
                Order ID
              </th>
              <th className='min-w-[200px] px-4 py-2 font-medium text-dark dark:text-white'>
                Reason
              </th>
              <th className='min-w-[120px] px-4 py-2 font-medium text-dark dark:text-white'>
                Status
              </th>
              <th className='min-w-[150px] px-4 py-2 font-medium text-dark dark:text-white'>
                Requested At
              </th>
              <th className='min-w-[120px] px-4 py-2 text-right font-medium text-dark dark:text-white xl:pr-7.5'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {props.refundRequests?.map((request, index) => (
              <tr key={index} className='text-sm'>
                <td
                  className={`border-[#eee] px-4 py-2 dark:border-dark-3 xl:pl-7.5 ${
                    index === props.refundRequests.length - 1
                      ? "border-b-0"
                      : "border-b"
                  }`}
                >
                  <h5 className='my-0 text-dark dark:text-white'>
                    {request.orderId?._id || request.orderId || ""}
                  </h5>
                </td>
                <td
                  className={`border-[#eee] px-4 py-2 dark:border-dark-3 ${
                    index === props.refundRequests.length - 1
                      ? "border-b-0"
                      : "border-b"
                  }`}
                >
                  <p className='text-dark dark:text-white'>
                    {request.reason || "-"}
                  </p>
                </td>
                <td
                  className={`border-[#eee] px-4 py-2 dark:border-dark-3 ${
                    index === props.refundRequests.length - 1
                      ? "border-b-0"
                      : "border-b"
                  }`}
                >
                  {getStatusBadge(request.status)}
                </td>
                <td
                  className={`border-[#eee] px-4 py-2 dark:border-dark-3 ${
                    index === props.refundRequests.length - 1
                      ? "border-b-0"
                      : "border-b"
                  }`}
                >
                  <p className='text-dark dark:text-white'>
                    {moment(request?.createdAt).format("D MMM YYYY, hh:mm A")}
                  </p>
                </td>
                <td
                  className={`border-[#eee] px-4 py-2 dark:border-dark-3 xl:pr-7.5 ${
                    index === props.refundRequests.length - 1
                      ? "border-b-0"
                      : "border-b"
                  }`}
                >
                  <div className='flex items-center justify-end space-x-3.5'>
                    {request.status === "pending" && (
                      <div className='group relative'>
                        <button
                          className='rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700'
                          onClick={() => handleApproveClick(request)}
                        >
                          Approve
                        </button>
                      </div>
                    )}
                    {request.status !== "pending" && (
                      <span className='text-xs text-gray-400 capitalize'>
                        {request.status}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        centered
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title='Approve Refund Request'
        overlayProps={{
          backgroundOpacity: 0.6,
          blur: 3,
        }}
      >
        <div className='my-8 text-center'>
          <h1 className='mb-4 text-xl font-semibold'>
            Approve Refund Request?
          </h1>
          <p className='mb-6 text-sm text-gray-500'>
            This will process the Stripe refund, update payment status to
            "refunded", and update the order status to "refunded". This action
            cannot be undone.
          </p>
        </div>
        <div className='flex items-center justify-center space-x-4'>
          <button
            className='w-full rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700'
            onClick={handleApprove}
            disabled={approveLoading}
          >
            {approveLoading ? (
              <BiLoaderAlt className='m-auto animate-spin text-2xl' />
            ) : (
              "Approve"
            )}
          </button>
          <button
            className='w-full rounded bg-gray-300 px-4 py-2 hover:bg-gray-400'
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({ loading: state?.app?.visible });
export default connect(mapStateToProps, { approveRefundRequest })(
  RefundRequestTable
);
